import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Download, Calendar, X } from 'lucide-react';

export function SearchBar({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div className="relative" data-testid="search-bar">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#000B18] border border-white/15 text-white text-sm placeholder-[#5C7599] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-colors"
        data-testid="search-input"
      />
    </div>
  );
}

export function Pagination({ skip, limit, total, onPageChange, onLimitChange, showPerPage = true }) {
  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const perPageOptions = [10, 15, 20, 25, 50, 100];

  const goToPage = (page) => {
    const newSkip = (page - 1) * limit;
    onPageChange(newSkip);
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  if (total <= 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm mt-5" data-testid="pagination">
      <div className="flex items-center gap-3 text-[#5C7599]">
        <span>Showing {skip + 1}-{Math.min(skip + limit, total)} of {total}</span>
        {showPerPage && onLimitChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-[#5C7599]">Per page:</span>
            <select
              value={limit}
              onChange={(e) => onLimitChange(parseInt(e.target.value))}
              className="bg-[#000B18] border border-white/15 text-white rounded px-2 py-1 text-xs focus:outline-none focus:border-[#00E5FF]/50"
              data-testid="per-page-select"
            >
              {perPageOptions.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#A0B4CC] hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="prev-page-btn"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {getPageNumbers().map((p, i) =>
          p === '...' ? (
            <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-[#5C7599]">...</span>
          ) : (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors ${
                p === currentPage
                  ? 'bg-[#00E5FF] text-[#000B18] font-semibold'
                  : 'text-[#A0B4CC] hover:bg-white/10'
              }`}
              data-testid={`page-${p}-btn`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#A0B4CC] hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="next-page-btn"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function FilterSelect({ value, onChange, placeholder, options = [], 'data-testid': testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#000B18] border border-white/15 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 min-w-[140px]"
      data-testid={testId || 'filter-select'}
    >
      <option value="">{placeholder}</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

const DATE_PRESETS = [
  { label: 'Today', key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: '7 Days', key: '7d' },
  { label: '1 Month', key: '1m' },
  { label: '3 Months', key: '3m' },
  { label: '6 Months', key: '6m' },
  { label: '9 Months', key: '9m' },
  { label: '1 Year', key: '1y' },
  { label: 'Custom', key: 'custom' },
];

function calcDateRange(key) {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  const daysAgo = (n) => {
    const d = new Date(now);
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
  };

  switch (key) {
    case 'today': return { from: todayStr + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case 'yesterday': return { from: yesterdayStr + 'T00:00:00', to: yesterdayStr + 'T23:59:59' };
    case '7d': return { from: daysAgo(7) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case '1m': return { from: daysAgo(30) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case '3m': return { from: daysAgo(90) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case '6m': return { from: daysAgo(180) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case '9m': return { from: daysAgo(270) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    case '1y': return { from: daysAgo(365) + 'T00:00:00', to: todayStr + 'T23:59:59' };
    default: return { from: '', to: '' };
  }
}

export function DateRangeFilter({ dateFrom, dateTo, onDateFromChange, onDateToChange, onPresetChange }) {
  const [activePreset, setActivePreset] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const handlePreset = (key) => {
    setActivePreset(key);
    if (key === 'custom') {
      setShowCustom(true);
      return;
    }
    if (key === '') {
      setShowCustom(false);
      onDateFromChange('');
      onDateToChange('');
      if (onPresetChange) onPresetChange('');
      return;
    }
    setShowCustom(false);
    const range = calcDateRange(key);
    onDateFromChange(range.from);
    onDateToChange(range.to);
    if (onPresetChange) onPresetChange(key);
  };

  const clearFilter = () => {
    setActivePreset('');
    setShowCustom(false);
    onDateFromChange('');
    onDateToChange('');
    if (onPresetChange) onPresetChange('');
  };

  return (
    <div className="space-y-2" data-testid="date-range-filter">
      <div className="flex flex-wrap items-center gap-1.5">
        <Calendar className="w-4 h-4 text-[#5C7599] mr-1" />
        {DATE_PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => handlePreset(p.key)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              activePreset === p.key
                ? 'bg-[#00E5FF] text-[#000B18]'
                : 'bg-[#000B18] border border-white/10 text-[#A0B4CC] hover:border-white/20'
            }`}
            data-testid={`date-preset-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        {activePreset && (
          <button
            onClick={clearFilter}
            className="px-2 py-1 rounded-md text-xs bg-[#FF1744]/10 text-[#FF1744] hover:bg-[#FF1744]/20 transition-colors"
            data-testid="date-clear-btn"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {showCustom && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom ? dateFrom.split('T')[0] : ''}
            onChange={(e) => onDateFromChange(e.target.value ? e.target.value + 'T00:00:00' : '')}
            className="bg-[#000B18] border border-white/15 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]/50"
            data-testid="custom-date-from"
          />
          <span className="text-[#5C7599] text-xs">to</span>
          <input
            type="date"
            value={dateTo ? dateTo.split('T')[0] : ''}
            onChange={(e) => onDateToChange(e.target.value ? e.target.value + 'T23:59:59' : '')}
            className="bg-[#000B18] border border-white/15 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]/50"
            data-testid="custom-date-to"
          />
        </div>
      )}
    </div>
  );
}
